import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompatibilityFindings } from '@/tools/models/CompatibilityFindings.molecule';
import { hasBreakingFindings } from '@/tools/models/compatibilityDiff.utilities';
import { Modal } from '@seeqdev/qomponents';
import type { ModelCompatibilityFindingOutputV1 } from '@seeqdev/api/model/ModelCompatibilityFindingOutputV1';

interface LockModelConfirmationProps {
  onConfirm: () => Promise<void>;
  onClose: () => void;
  /** Compatibility findings to display inline. When present, findings are shown below the lock message. */
  findings?: ModelCompatibilityFindingOutputV1[];
  /** Map of canonicalId → human-readable feature name for findings display. */
  featureNamesByCanonicalId?: Map<string, string>;
}

/**
 * Confirmation dialog shown before locking a model. Locking is permanent
 * and makes the model read-only. The user must check an acknowledgment
 * checkbox before the Lock button is enabled.
 *
 * When compatibility findings are provided, they are displayed inline below the
 * lock warning. If breaking changes exist, the checkbox text combines the lock
 * and breaking acknowledgments into a single checkbox.
 */
export const LockModelConfirmation = ({
  onConfirm,
  onClose,
  findings = [],
  featureNamesByCanonicalId = new Map(),
}: LockModelConfirmationProps) => {
  const { t } = useTranslation();
  const [understood, setUnderstood] = useState(false);
  const [processing, setProcessing] = useState(false);

  const hasBreaking = hasBreakingFindings(findings);

  const checkboxLabel = hasBreaking
    ? t('MODELS.LOCK_CONFIRMATION_CHECKBOX_WITH_BREAKING')
    : t('MODELS.LOCK_CONFIRMATION_CHECKBOX');

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal
      open={true}
      title={t('MODELS.LOCK_CONFIRMATION_TITLE')}
      onClose={onClose}
      onSubmit={handleConfirm}
      submitButtonLabel={t('MODELS.LOCK_MODEL')}
      cancelButtonLabel={t('CANCEL')}
      disableSubmitButton={!understood || processing}
      size="md">
      <div data-testid="lockModelConfirmation">
        <p className="tw:mb-4">{t('MODELS.LOCK_CONFIRMATION_MESSAGE')}</p>

        {findings.length > 0 && (
          <div className="tw:mb-4">
            <CompatibilityFindings findings={findings} featureNamesByCanonicalId={featureNamesByCanonicalId} />
          </div>
        )}

        <label className="tw:flex tw:items-start tw:gap-2 tw:cursor-pointer">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
            className="tw:mt-1"
            data-testid="lockConfirmCheckbox"
          />
          <span>{checkboxLabel}</span>
        </label>
      </div>
    </Modal>
  );
};
